import { CANVAS_HEIGHT_PX, CANVAS_WIDTH_PX, type ViewState } from "./canvas-coordinates";
import { deriveViewFromCenterStage, type CanvasViewUrlState } from "./canvas-view-url";

// キャンバスを開いたときの初期表示（view）を決めるユーティリティ。
// 決定規則を CanvasStage の effect から切り離し、ブラウザー無しの単体テストで検証できるようにする。
//
// 決定順は「共有 URL → （編集ビューのみ）保存済み表示位置 → キャンバス中央・等倍」の 1 本で、
// 参照専用（Connect Note の iframe 埋め込み）専用の分岐は持たない。埋め込みでも本体と同じ初期表示
// （キャンバス中央・等倍）にすることで、記事へ埋め込んだキャンバスが内容の広がりに応じて強く縮小され、
// 付箋の文字が読めない大きさで開く問題を避ける。

/** 共有 URL も保存済み表示位置も無い初回表示で使う既定ズーム倍率（等倍）。 */
export const DEFAULT_ZOOM = 1;

/** 初期ビューをどの入力から決めたか。 */
export type InitialCanvasViewSource = "url" | "saved" | "center";

export interface InitialCanvasViewInput {
  /** 共有 URL から読み取った位置指定。無ければ null（`readCanvasViewFromUrl` の戻り値をそのまま渡す）。 */
  readonly fromUrl: CanvasViewUrlState | null;
  /** 参照専用（Connect Note の iframe 埋め込み）か。 */
  readonly readOnly: boolean;
  /**
   * 保存済み表示位置の取得。参照専用では呼び出さない（分離ストレージへ触れないことが契約）。
   * 呼び出し側は `() => loadCanvasView(canvasId)` を渡す。
   */
  readonly loadSavedView: () => ViewState | null;
  /** 表示領域の寸法。 */
  readonly viewport: { readonly width: number; readonly height: number };
}

export interface InitialCanvasViewResult {
  readonly view: ViewState;
  /** 共有 URL 由来か、保存位置の復元か、既定（キャンバス中央・等倍）か。 */
  readonly source: InitialCanvasViewSource;
}

/**
 * 初期表示の view を決める（純粋関数）。
 *
 * - 共有 URL（`?cx` / `?cy` / `?z`）の位置指定があれば、参照専用かどうかに関わらず最優先で採用する。
 * - 参照専用では保存済み表示位置を読み出さない。iframe ではストレージが分離され、本体で保存した位置を
 *   参照できないため、分離ストレージ側に残った古い位置を復元しても利用者にとって意味が無く、初回表示の
 *   決定性も失われる。`loadSavedView` を値ではなく取得関数で受け取っているのは、参照専用で
 *   **一度も呼ばない**ことをテストで検証できるようにするため。
 * - いずれも無ければ、左上原点ではなくキャンバス中央を画面中央へ合わせた等倍表示にする。
 */
export function resolveInitialCanvasView(input: InitialCanvasViewInput): InitialCanvasViewResult {
  const { fromUrl, readOnly, loadSavedView, viewport } = input;
  if (fromUrl !== null) {
    return {
      view: deriveViewFromCenterStage(
        fromUrl.centerStageX,
        fromUrl.centerStageY,
        fromUrl.zoom,
        viewport
      ),
      source: "url",
    };
  }
  if (!readOnly) {
    const saved = loadSavedView();
    if (saved !== null) {
      return { view: saved, source: "saved" };
    }
  }
  return {
    view: deriveViewFromCenterStage(
      CANVAS_WIDTH_PX / 2,
      CANVAS_HEIGHT_PX / 2,
      DEFAULT_ZOOM,
      viewport
    ),
    source: "center",
  };
}
